import { useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table'
import { EyeIcon, ListFilter } from 'lucide-react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import {
  EducationLevel,
  MaritalStatus,
  SearchUserParticipantRequestV2,
  SearchUserParticipantV2Query,
  StatusProfile,
} from '@/_gql/graphql'

import { queryGetParticipantsV2 } from '@/services/admin/query'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTableColumnHeader } from '@/components/ui/data-table/column-header'
import { DataTable } from '@/components/ui/data-table/data-table'
import { DatePickerRange } from '@/components/ui/date-picker-range'
import { DebouncedInput } from '@/components/ui/debounce-input'
import { Form, FormField } from '@/components/ui/form'
import { FormFieldset } from '@/components/ui/form-field/fieldset'
import { FormFieldSelect } from '@/components/ui/form-field/select'
import { Sheet, SheetResponsive, SheetTrigger } from '@/components/ui/sheet'

import { fuzzyFilterFn } from '@/lib/utils/datatable'

import { dateFormat } from '@/lib/formatter'
import {
  EDUCATION_LEVEL_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RANGE_AGE,
  STATUS_PROFILE_OPTIONS,
  TOTAL_EXPERIENCE_INMONTH,
} from '../constant'
import { ChangeStatusDialog } from './change-status-dialog'

type User =
  SearchUserParticipantV2Query['searchUserParticipantV2']['data'][number]

export function TableParticipants() {
  const [openFilter, setOpenFilter] = useState(false)
  const [filter, setFilter] = useState<FilterDto>({})
  const [openChangeStatus, setOpenStatus] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  useEffect(() => {
    setRowSelection({})
  }, [pagination, search, filter, setRowSelection])

  useEffect(() => {
    setPagination((x) => ({ ...x, pageIndex: 0 }))
  }, [search, filter])

  const params = useMemo((): SearchUserParticipantRequestV2 => {
    const age = filter?.range_age ? search.split('-') : null
    return {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: search.length ? search : undefined,
      education_level: (filter?.education_level as EducationLevel) || undefined,
      marital_status: (filter?.marital_status as MaritalStatus) || undefined,
      max_age: age && age.length === 2 ? Number(age[1]) : undefined,
      min_age: age && age.length === 2 ? Number(age[0]) : undefined,
      total_experience_in_months: Number(filter?.total_experience) || undefined,
      ...(filter.applyDate?.length === 2
        ? {
            start_apply_date: dateFormat(filter.applyDate[0], 'yyyy-MM-dd'),
            end_apply_date: dateFormat(filter.applyDate[1], 'yyyy-MM-dd'),
          }
        : {}),
      ...(filter.registerDate?.length === 2
        ? {
            start_register_date: dateFormat(
              filter.registerDate[0],
              'yyyy-MM-dd'
            ),
            end_register_date: dateFormat(filter.registerDate[1], 'yyyy-MM-dd'),
          }
        : {}),
    }
  }, [pagination, search, filter])

  const { data, isLoading } = useQuery(queryGetParticipantsV2(params))

  const columns = useMemo(getColumns, [])
  const pageCount = useMemo((): number => {
    if (!data?.pagination.total) return 0
    return Math.ceil(data.pagination.total / pagination.pageSize)
  }, [data?.pagination.total, pagination])

  const table = useReactTable({
    data: data?.data || [],
    pageCount: pageCount,
    columns,
    state: { pagination, rowSelection },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    filterFns: {
      fuzzy: fuzzyFilterFn,
    },
    getRowId: (row) => String(row.id),
    globalFilterFn: 'fuzzy',
  })

  const selectedCount = Object.keys(rowSelection).length

  return (
    <div className="space-y-2.5">
      <ChangeStatusDialog
        open={openChangeStatus}
        setOpen={setOpenStatus}
        getData={() => {
          const rowIds = new Set(Object.keys(rowSelection))
          const users = data?.data?.filter((d) => rowIds.has(String(d.id)))
          return users || []
        }}
        onSuccess={function (): void {}}
      />
      <div className="flex items-center justify-between">
        <div>
          <DebouncedInput
            value={search}
            onChange={(value) => setSearch(String(value))}
            placeholder="Cari .."
          />
        </div>
        <div className="flex items-center justify-center gap-2">
          {selectedCount > 0 && (
            <>
              <Button
                variant={'outline_yes'}
                onClick={() => setOpenStatus(true)}
              >
                ( {selectedCount} ) Ubah Status
              </Button>
              <Button onClick={() => setRowSelection({})}>Batalkan</Button>
            </>
          )}
          <FilterTable
            open={openFilter}
            setOpen={setOpenFilter}
            onSubmit={setFilter}
            initialValues={filter}
          />
        </div>
      </div>
      <DataTable table={table} isLoading={isLoading} />
    </div>
  )
}

type FilterDto = z.infer<typeof FilterDto>
const FilterDto = z.object({
  marital_status: z.nativeEnum(MaritalStatus).or(z.string()).optional(),
  total_experience: z.string().optional(),
  education_level: z.nativeEnum(EducationLevel).or(z.string()).optional(),
  range_age: z.string().optional(),
  registerDate: z
    .tuple([z.coerce.date(), z.coerce.date()])
    .nullable()
    .optional(),
  applyDate: z.tuple([z.coerce.date(), z.coerce.date()]).nullable().optional(),
})

interface FilterTableProps {
  open: boolean
  setOpen: (state: boolean) => void
  initialValues?: FilterDto
  onSubmit: (data: FilterDto) => void
}

export function FilterTable(props: FilterTableProps) {
  const { open, setOpen, initialValues = {}, onSubmit } = props
  const form = useForm<FilterDto>({
    resolver: zodResolver(FilterDto),
    defaultValues: getDefaultFilter(initialValues),
  })

  useEffect(() => {
    if (!open) return
    form.reset(getDefaultFilter(initialValues))
  }, [initialValues, open])

  const handleOnSubmit = form.handleSubmit(async (data) => {
    onSubmit(data)
    setOpen(false)
  })

  const clearFilter = () => {
    onSubmit(getDefaultFilter())
    form.reset(getDefaultFilter())
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={(state) => setOpen(state)}>
      <SheetTrigger asChild>
        <Button variant={'outline_yes'}>
          <ListFilter /> Filter
        </Button>
      </SheetTrigger>
      <SheetResponsive className="p-0">
        <Form {...form}>
          <div className="relative flex max-h-[70vh] w-full flex-1 flex-col gap-4 overflow-y-auto px-4 py-8 md:max-h-full">
            <FormField
              control={form.control}
              name={`marital_status`}
              render={({ field }) => (
                <FormFieldset label="Status Pernikahan" className="w-full">
                  <FormFieldSelect
                    className="w-full"
                    placeholder="Sudah menikah / belum"
                    items={MARITAL_STATUS_OPTIONS}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />
            <FormField
              control={form.control}
              name={`total_experience`}
              render={({ field }) => (
                <FormFieldset
                  label="Jumlah Pengalaman Kerja"
                  className="w-full"
                >
                  <FormFieldSelect
                    className="w-full"
                    placeholder="Dibawah 6 bulan / 1 tahun / 1.5 tahun "
                    items={TOTAL_EXPERIENCE_INMONTH}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />
            <FormField
              control={form.control}
              name={`education_level`}
              render={({ field }) => (
                <FormFieldset label="Pendidikan Terakhir" className="w-full">
                  <FormFieldSelect
                    className="w-full"
                    placeholder="S1 / D4 / SMK"
                    items={EDUCATION_LEVEL_OPTIONS}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />

            <FormField
              control={form.control}
              name={`range_age`}
              render={({ field }) => (
                <FormFieldset label="Usia" className="w-full">
                  <FormFieldSelect
                    className="w-full"
                    placeholder="18-24 Tahun / 26-30 Tahun / 31 Tahun ke ..."
                    items={RANGE_AGE}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                </FormFieldset>
              )}
            />

            <FormField
              control={form.control}
              name={`applyDate`}
              render={({ field }) => (
                <FormFieldset label="Tanggal Apply">
                  <DatePickerRange
                    buttonClassName="w-full"
                    value={
                      field.value
                        ? {
                            from: field.value[0],
                            to: field.value[1],
                          }
                        : undefined
                    }
                    onChange={(value) => {
                      if (!value) field.onChange(undefined)
                      const { from, to } = value || {}
                      field.onChange([from, to || from])
                    }}
                  />
                </FormFieldset>
              )}
            />

            <FormField
              control={form.control}
              name={'registerDate'}
              render={({ field }) => (
                <FormFieldset label="Tanggal Daftar">
                  <DatePickerRange
                    buttonClassName="w-full"
                    value={
                      field.value
                        ? {
                            from: field.value[0],
                            to: field.value[1],
                          }
                        : undefined
                    }
                    onChange={(value) => {
                      if (!value) field.onChange(undefined)
                      const { from, to } = value || {}
                      field.onChange([from, to || from])
                    }}
                  />
                </FormFieldset>
              )}
            />
          </div>
          <div className="z-20 flex justify-end gap-4 p-4">
            <Button type="button" variant={'outline_yes'} onClick={clearFilter}>
              Hapus Filter
            </Button>
            <Button
              onClick={handleOnSubmit}
              className="bg-primary-yes hover:bg-primary-yes"
            >
              Terapkan Filter
            </Button>
          </div>
        </Form>
      </SheetResponsive>
    </Sheet>
  )
}

function getColumns(): ColumnDef<User>[] {
  return [
    {
      id: 'selection',
      accessorFn: (row) => row.id,
      header: ({ table }) => {
        return (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            onCheckedChange={table.getToggleAllRowsSelectedHandler()}
          />
        )
      },
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={row.getToggleSelectedHandler()}
          />
        )
      },
    },
    { accessorKey: 'id', header: 'ID' },
    {
      accessorKey: 'name',
      meta: {
        columnName: 'Name',
      },
      filterFn: 'includesStringSensitive',
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
    },
    {
      accessorKey: 'email',
      meta: {
        columnName: 'Email',
      },
      filterFn: 'includesStringSensitive',
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
    },
    {
      accessorKey: 'handphone_number',
      meta: {
        columnName: 'Nomor HP',
      },
      filterFn: 'includesStringSensitive',
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
    },
    {
      accessorKey: 'user_personal_info.birthdate',
      meta: {
        columnName: 'Tanggal Lahir',
      },
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
    },
    {
      accessorKey: 'user_education',
      meta: {
        columnName: 'Pendidikan Terakhir',
      },
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
      cell: ({ cell }) => {
        const { user_education } = cell.row.original
        return <>{getLastEduction(user_education)}</>
      },
    },
    {
      accessorKey: 'user_personal_info.address',
      meta: {
        columnName: 'Domisili',
      },
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
    },
    {
      accessorKey: 'user_status_profile.status',
      meta: {
        columnName: 'Status',
      },
      header: ({ column }) => {
        return <DataTableColumnHeader column={column} />
      },
      cell: ({ cell }) => {
        const { status } = cell.row.original.user_status_profile || {}
        return <>{BadgeStatus(status)}</>
      },
    },
    {
      id: 'actions',
      header: 'Aksi',
      cell: ({ cell }) => {
        const { id, name } = cell.row.original
        return (
          <div className="flex gap-1.5">
            <Button
              size="icon"
              className="size-8 text-primary-yes"
              variant="ghost"
              asChild
            >
              <Link
                to={'/admin-v2/participant/$participantId'}
                params={{ participantId: String(id) }}
              >
                <EyeIcon />
              </Link>
            </Button>
          </div>
        )
      },
    },
  ]
}

export function BadgeStatus(status?: string) {
  const getStatus =
    status && status.toUpperCase()
      ? status.toUpperCase()
      : StatusProfile.FillingSubmissionStage
  let classNameBackground = ''
  const getLabel = STATUS_PROFILE_OPTIONS.find(
    (status) => status.value == getStatus
  )
  switch (getStatus) {
    case StatusProfile.FillingSubmissionStage:
      classNameBackground = 'bg-[#FFF494] text-[#978803] hover:bg-[#FFF494]'
      break
    case StatusProfile.SubmissionApproved:
      classNameBackground = 'bg-[#CDFFD2] text-[#029E4A] hover:bg-[#CDFFD2]'
      break
    case StatusProfile.SubmissionRejected:
      classNameBackground = 'bg-[#FFCDD1] text-[#BC0000] hover:bg-[#FFCDD1]'
      break
    case StatusProfile.SubmissionVerificationStage:
      classNameBackground = 'bg-[#A9EAFF] text-[#0093C2] hover:bg-[#A9EAFF]'
      break
    default:
      classNameBackground = 'bg-[#FFF494] text-[#978803] hover:bg-[#FFF494]'
  }

  return <Badge className={classNameBackground}>{getLabel?.label}</Badge>
}

function getLastEduction(user_education: User['user_education']) {
  if (user_education && user_education.length > 0) {
    const levelEducation = user_education[user_education.length - 1]?.level
    const findLevelEducation = EDUCATION_LEVEL_OPTIONS.find(
      (edu) => edu.value === levelEducation?.toUpperCase()
    )
    return findLevelEducation?.label
  }

  return ''
}

function getDefaultFilter(init?: FilterDto): FilterDto {
  return (
    init ?? {
      applyDate: null,
      education_level: '',
      marital_status: '',
      range_age: '',
      registerDate: null,
      total_experience: '',
    }
  )
}
